/**
 * ============================================================
 * DATABASE ADAPTER
 * ============================================================
 * Currently: localStorage (development / demo)
 *
 * TO SWITCH TO SUPABASE:
 *   1. npm install @supabase/supabase-js
 *   2. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY in .env
 *   3. Replace LocalStorageAdapter with SupabaseAdapter (see bottom of file)
 *   4. Run the SQL schema in /server/schema.sql against your Supabase project
 *
 * TO SWITCH TO FIREBASE:
 *   1. npm install firebase
 *   2. Replace LocalStorageAdapter with FirebaseAdapter
 *   3. See /server/firebase-config.ts for setup
 * ============================================================
 */

import { User, Mission, Chat, Report, Message } from '../types';
import { generateId } from './security';

export interface DB {
  users: User[];
  missions: Mission[];
  chats: Record<string, Chat>;
  reports: Report[];
}

const DB_KEY = 'mm_db_v2';

// ---- Seed data ----
function createSeedDB(): DB {
  return {
    users: [
      {
        id: 'seed-u1', firstName: 'Elena', lastName: 'Vasquez',
        email: 'elena@example.com', country: 'France', city: 'Paris',
        bio: 'Fast, reliable, multilingual. I get things done in Paris.',
        avatar: null, skills: [
          { name: 'French', level: 10 }, { name: 'Driving', level: 8 }, { name: 'Negotiation', level: 7 },
        ], rating: 4.8, ratingCount: 23, reports: [], blocked: false, role: 'user',
        createdAt: Date.now() - 86400000 * 30, updatedAt: Date.now(),
      },
      {
        id: 'seed-u2', firstName: 'Marco', lastName: 'Bernardi',
        email: 'marco@example.com', country: 'France', city: 'Lyon',
        bio: 'Tech wizard, app tester, occasional errand runner.',
        avatar: null, skills: [
          { name: 'Tech', level: 9 }, { name: 'App Testing', level: 10 }, { name: 'Research', level: 8 },
        ], rating: 4.5, ratingCount: 11, reports: [], blocked: false, role: 'user',
        createdAt: Date.now() - 86400000 * 20, updatedAt: Date.now(),
      },
      {
        id: 'seed-u3', firstName: 'Sophie', lastName: 'Dubois',
        email: 'sophie@example.com', country: 'France', city: 'Bordeaux',
        bio: 'Graphic designer, creative thinker, problem solver.',
        avatar: null, skills: [
          { name: 'Design', level: 9 }, { name: 'Creative Writing', level: 8 }, { name: 'Social Media', level: 7 },
        ], rating: 4.9, ratingCount: 34, reports: [], blocked: false, role: 'user',
        createdAt: Date.now() - 86400000 * 10, updatedAt: Date.now(),
      },
    ],
    missions: [
      {
        id: 'seed-m1', title: "Buy organic groceries at Marché d'Aligre",
        description: "Need someone to buy a specific list of organic items from the Marché d'Aligre market. Will provide the list and payment for the goods. Delivery to 15th arrondissement.",
        reward: 45, estimatedHours: 2, category: 'Errands', location: 'Paris, France',
        skills: ['Errands', 'French'], posterId: 'seed-u2', status: 'open',
        applicantIds: [], hiredId: null, payments: { hired: 0, started: 0, finished: 0 },
        stripePaymentIntentId: null, createdAt: Date.now() - 86400000, updatedAt: Date.now(),
      },
      {
        id: 'seed-m2', title: 'Test new iOS productivity app & write UX review',
        description: "Looking for someone to spend 3 hours testing our new iOS productivity app and writing a detailed review of the UX/UI and functionality. Must have an iPhone.",
        reward: 80, estimatedHours: 3, category: 'Tech', location: 'Remote',
        skills: ['App Testing', 'iOS', 'Writing'], posterId: 'seed-u3', status: 'open',
        applicantIds: [], hiredId: null, payments: { hired: 0, started: 0, finished: 0 },
        stripePaymentIntentId: null, createdAt: Date.now() - 3600000, updatedAt: Date.now(),
      },
      {
        id: 'seed-m3', title: 'Drive me to CDG airport early Friday morning',
        description: "Need a reliable driver from the 15th arrondissement to CDG Terminal 2E. Departure at 4:30 AM Friday. Must be punctual, clean car required.",
        reward: 60, estimatedHours: 1.5, category: 'Transport', location: 'Paris, France',
        skills: ['Driving', 'French'], posterId: 'seed-u1', status: 'active',
        applicantIds: ['seed-u2'], hiredId: 'seed-u2',
        payments: { hired: 6, started: 24, finished: 0 },
        stripePaymentIntentId: 'pi_demo_001', createdAt: Date.now() - 7200000, updatedAt: Date.now(),
      },
      {
        id: 'seed-m4', title: 'Competitive analysis for SaaS startup',
        description: "I need a thorough competitive analysis of 5 SaaS tools in the project management space. Deliverable: a structured report with screenshots, pricing tables, and feature comparison.",
        reward: 120, estimatedHours: 5, category: 'Research', location: 'Remote',
        skills: ['Research', 'Writing', 'Tech'], posterId: 'seed-u3', status: 'open',
        applicantIds: [], hiredId: null, payments: { hired: 0, started: 0, finished: 0 },
        stripePaymentIntentId: null, createdAt: Date.now() - 172800000, updatedAt: Date.now(),
      },
    ],
    chats: {},
    reports: [],
  };
}

// ---- LocalStorage Adapter ----

class LocalStorageAdapter {
  private db: DB;

  constructor() {
    try {
      const raw = localStorage.getItem(DB_KEY);
      this.db = raw ? JSON.parse(raw) : createSeedDB();
    } catch {
      this.db = createSeedDB();
    }
  }

  private save() {
    try {
      localStorage.setItem(DB_KEY, JSON.stringify(this.db));
    } catch (e) {
      console.error('[DB] Save failed:', e);
    }
  }

  // --- Users ---
  getUsers(): User[] { return this.db.users; }
  getUserById(id: string): User | undefined { return this.db.users.find((u) => u.id === id); }
  getUserByEmail(email: string): User | undefined {
    return this.db.users.find((u) => u.email === email.toLowerCase());
  }
  createUser(data: Omit<User, 'id' | 'createdAt' | 'updatedAt'>): User {
    const user: User = { ...data, id: generateId(), createdAt: Date.now(), updatedAt: Date.now() };
    this.db.users.push(user);
    this.save();
    return user;
  }
  updateUser(id: string, data: Partial<User>): User | null {
    const idx = this.db.users.findIndex((u) => u.id === id);
    if (idx < 0) return null;
    this.db.users[idx] = { ...this.db.users[idx], ...data, updatedAt: Date.now() };
    this.save();
    return this.db.users[idx];
  }

  // --- Missions ---
  getMissions(): Mission[] { return this.db.missions; }
  getMissionById(id: string): Mission | undefined { return this.db.missions.find((m) => m.id === id); }
  createMission(data: Omit<Mission, 'id' | 'createdAt' | 'updatedAt'>): Mission {
    const mission: Mission = { ...data, id: generateId(), createdAt: Date.now(), updatedAt: Date.now() };
    this.db.missions.push(mission);
    this.save();
    return mission;
  }
  updateMission(id: string, data: Partial<Mission>): Mission | null {
    const idx = this.db.missions.findIndex((m) => m.id === id);
    if (idx < 0) return null;
    this.db.missions[idx] = { ...this.db.missions[idx], ...data, updatedAt: Date.now() };
    this.save();
    return this.db.missions[idx];
  }

  // --- Chats ---
  getChats(): Record<string, Chat> { return this.db.chats; }
  getChatById(id: string): Chat | undefined { return this.db.chats[id]; }
  getChatsByUser(userId: string): Chat[] {
    return Object.values(this.db.chats).filter((c) => c.participantIds.includes(userId));
  }
  createChat(data: Omit<Chat, 'id'> & { id?: string }): Chat {
    const chat: Chat = { ...data, id: data.id ?? generateId() };
    this.db.chats[chat.id] = chat;
    this.save();
    return chat;
  }
  addMessage(chatId: string, msg: Omit<Message, 'id'>): Message | null {
    const chat = this.db.chats[chatId];
    if (!chat) return null;
    const message: Message = { ...msg, id: generateId() };
    chat.messages.push(message);
    chat.lastMessageAt = message.ts;
    this.save();
    return message;
  }
  updateChat(id: string, data: Partial<Chat>): Chat | null {
    if (!this.db.chats[id]) return null;
    this.db.chats[id] = { ...this.db.chats[id], ...data };
    this.save();
    return this.db.chats[id];
  }

  // --- Reports ---
  createReport(data: Omit<Report, 'id'>): Report {
    const report: Report = { ...data, id: generateId() };
    this.db.reports.push(report);
    this.save();
    return report;
  }

  reset() { this.db = createSeedDB(); this.save(); }
}

export const db = new LocalStorageAdapter();

/*
=================================================================
SUPABASE ADAPTER STUB
=================================================================
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL!,
  process.env.REACT_APP_SUPABASE_ANON_KEY!
);

export const db = {
  async getUsers() {
    const { data } = await supabase.from('users').select('*');
    return data ?? [];
  },
  async getUserById(id: string) {
    const { data } = await supabase.from('users').select('*').eq('id', id).single();
    return data;
  },
  // ... etc.
};
=================================================================
*/
